import React, { useEffect, useState } from 'react';
import { List, ListItem } from './styled';
import Text from '@service-victoria/component-library/components/Text';
import IconList from '@service-victoria/component-library/components/IconList/controlled/Formik';
import { ExternalLinks, ReturningHomeEndDate } from 'utils/constants';
import { useField } from 'formik';
import { PermitType } from './types';
import * as Styled from 'steps/styled';
import dayjs from 'dayjs';
import { TravelReasonSuccess } from 'interfaces/store';
import { memoize } from 'lodash';
import PermitService from 'services/permitService';
import ExemptionPreamble from 'steps/Assess/components/ExemptionForm/ExemptionPreamble';

type TravelZoneSelectorProps = {
  onSelect: (value: PermitType) => void;
  name: string;
  labelID?: string;
  travelReasons: TravelReasonSuccess;
};

const iconListContainerStyle = { maxWidth: '520px' };
const iconListErrorProps = { 'data-testid': 'Zone-Error' };

const getZoneContent: (label?: string) => Record<PermitType, React.ReactElement | null> = memoize(
  label => ({
    green: (
      <>
        <Text.Heading.H3 fontWeight={600} id={label}>
          Select green zone permit if:
        </Text.Heading.H3>
        <List>
          <ListItem>
            you’ve only been in a green zone in the past two weeks prior to entry into Victoria, and
            haven’t been in an orange zone or red zone
          </ListItem>
          <ListItem>
            you’ve been in an orange zone or red zone only for direct and short-term transit to
            Victoria.
          </ListItem>
        </List>
      </>
    ),
    orange: (
      <>
        <Text.Heading.H3 fontWeight={600} id={label}>
          Select orange zone permit if:
        </Text.Heading.H3>
        <List>
          <ListItem>
            you’ve only been in an orange zone and not a red zone in the past two weeks prior to
            entry into Victoria
          </ListItem>
          <ListItem>
            or you’ve only been in a red zone for direct and short-term transit to Victoria.
          </ListItem>
        </List>
      </>
    ),
    red: (
      <>
        <Text.Heading.H3 fontWeight={600} id={label}>
          Select red zone permit if:
        </Text.Heading.H3>
        <List>
          <ListItem>
            you’ve only been in a red zone in the past two weeks prior to entry into Victoria
          </ListItem>
          <ListItem>
            or you’ve only been in a{' '}
            <Text.Link href={ExternalLinks.CoronavirusVicTravelPermitSystem} target='_blank'>
              very high risk exposure site
            </Text.Link>{' '}
            for direct and short-term transit to Victoria.
          </ListItem>
        </List>
      </>
    ),
    extreme: null,
    crossBorder: (
      <>
        <Text.Heading.H3 fontWeight={600} id={label}>
          Conditions of the Cross-border extreme risk zone permit
        </Text.Heading.H3>
        <Text.Body>
          Cross-border residents in an extreme risk zone aged 18 and older must have a cross border
          permit to enter Victoria and only for the following permitted reasons:
        </Text.Body>
        <List>
          <ListItem>
            necessary goods and services, such as medical care and getting a COVID-19 test
          </ListItem>
          <ListItem>getting a COVID-19 vaccination</ListItem>
          <ListItem>care and compassionate reasons</ListItem>
          <ListItem>
            paid or voluntary work (including for charitable and religious purposes)
          </ListItem>
          <ListItem>education (including childcare and early childhood services)</ListItem>
          <ListItem>visiting an intimate partner​</ListItem>
          <ListItem>moving house, inspecting a property or attending an auction</ListItem>
          <ListItem>
            driving someone who cannot drive themselves for one of the above reasons​
          </ListItem>
          <ListItem>
            travelling between places in Victoria where it is easier to travel through NSW
          </ListItem>
          <ListItem>
            travelling between places in NSW where it is easier to travel through Victoria.​
          </ListItem>
        </List>
        <Text.Body>
          You must carry your ID and proof you are a cross-border community resident.
        </Text.Body>
        <Text.Body>
          If you are not a Victorian resident, you cannot travel further than is reasonably
          necessary for the permitted reason for which you are entering Victoria.
        </Text.Body>
        <Text.Body>When in Victoria, check in using the Service Victoria App.</Text.Body>
        <Text.Heading.H3 fontWeight={600}>
          Your permit will be valid for 14 days from date of entry
        </Text.Heading.H3>
      </>
    ),
    transit: (
      <>
        <Text.Heading.H3 fontWeight={600} id={label}>
          Conditions of the transit permit
        </Text.Heading.H3>
        <List>
          <ListItem>
            you are briefly passing through Victoria, but not staying in Victoria and will not spend
            more than 24 hours in Victoria
          </ListItem>
          <ListItem>
            a transit permit is valid for 24 hours from the time and date that you first enter
            Victoria.
          </ListItem>
        </List>
      </>
    ),
    worker: (
      <>
        <Text.Heading.H3 fontWeight={600} id={label}>
          Type of permit
        </Text.Heading.H3>
        <Text.Body>
          Check which{' '}
          <Text.Link href={ExternalLinks.TravellersSpecifiedWorkerPermit} target='_blank'>
            Specified Worker Permit
          </Text.Link>{' '}
          applies to you
        </Text.Body>
      </>
    ),
    hotelQuarantine: (
      <>
        <Text.Heading.H3 fontWeight={600} id={label}>
          Conditions of the departing hotel quarantine permit
        </Text.Heading.H3>
        <List>
          <ListItem>you have completed hotel quarantine in any zone in Australia</ListItem>
          <ListItem>
            if you travel straight to Victoria after completing hotel quarantine you won&apos;t have
            to isolate. However, you need to get tested on day 3, 4 or 5 after leaving hotel
            quarantine
          </ListItem>
          <ListItem>
            those coming from red or extreme risk zones must travel directly to Victoria
          </ListItem>
          <ListItem>
            you may still enter on this permit if you don&apos;t travel from an orange or green zone
            after completing hotel quarantine. Please note there are different testing and
            quarantine conditions.
          </ListItem>
        </List>
      </>
    ),
    returning: (
      <>
        <Text.Body fontWeight={600} id={label}>
          You can apply for a returning home exemption if you’re a Victorian resident currently in a{' '}
          <Text.Link
            href={ExternalLinks.CrossBorderLgaList}
            value='cross-border community Local Government Area'
          />{' '}
          and have been in that area for the past 14 days from 25 August to 8 September 2021.
        </Text.Body>
        <Text.Body fontWeight={600}>
          Applications will be accepted for 7 days, closing at 6pm on Friday 17 September.
        </Text.Body>
        <Text.Body fontWeight={600}>
          If granted an exemption, you must enter Victoria by{' '}
          {dayjs(ReturningHomeEndDate).format('dddd D MMMM')}.
        </Text.Body>
        <Text.Body>
          Applying for a returning home exemption doesn’t guarantee entry into Victoria.
        </Text.Body>
        <Text.Body>
          You must meet all the following eligibility and public health requirements before an
          exemption can be granted:
        </Text.Body>
        <List>
          <ListItem>
            Provide proof of being in the current cross-border community area for the past 14 days
            from 25 August to 8 September 2021. Evidence of this will need to be provided through:
            <List>
              <ListItem>
                receipts or email confirmation from short-stay accommodation e.g. caravan park or
                AirBnB
              </ListItem>
              <ListItem>
                utility bill or driver licence of family or friends providing accommodation to you
                during this time
              </ListItem>
              <ListItem>shopping receipts or bank statement</ListItem>
              <ListItem>statutory declaration</ListItem>
            </List>
          </ListItem>
          <ListItem>
            Proof of ordinary place of residence in Victoria, including:
            <List>
              <ListItem>any government-issued document evidencing residency</ListItem>
              <ListItem>driver licence</ListItem>
              <ListItem>lease, rates notice or utility bill</ListItem>
              <ListItem>contract of sale</ListItem>
            </List>
          </ListItem>
          <ListItem>
            Evidence of a negative COVID-19 test result 48 hours prior to arrival. This evidence can
            be:
            <List>
              <ListItem>a screenshot of text message result, or</ListItem>
              <ListItem>an email from pathology provider</ListItem>
            </List>
          </ListItem>
          <ListItem>
            If eligible, evidence of a COVID-19 vaccination (single dose):
            <List>
              <ListItem>MyGov vaccination record</ListItem>
            </List>
          </ListItem>
          <ListItem>
            Evidence of isolating with minimal social contact while in NSW:
            <List>
              <ListItem>statutory declaration as to current living circumstances</ListItem>
            </List>
          </ListItem>
          <ListItem>
            Evidence of NSW LGAs visited in past 14 days:
            <List>
              <ListItem>screenshot of bank transactions</ListItem>
              <ListItem>statutory declaration of LGAs visited</ListItem>
            </List>
          </ListItem>
          <ListItem>
            Evidence of being able to safely isolate at home for 14 days once you have arrived in
            Victoria:
            <List>
              <ListItem>
                statutory declaration of proposed living circumstances and isolation plan
              </ListItem>
            </List>
          </ListItem>
        </List>
        <Text.Body>
          You will be required to undertake a COVID test on the day of your entry into Victoria at
          the nearest testing site on your route home, and on day 3 and 13 of your home isolation.
          You will be visited by Authorised Officers during your 14 day isolation period at home.
        </Text.Body>
        <Text.Body>
          You must not travel to Victoria until an exemption is granted in writing by the Department
          of Health.
        </Text.Body>
      </>
    ),
    international: null,
    exemption: <ExemptionPreamble />
  })
);

const permitService = new PermitService();

const TravelZoneSelector: React.FC<TravelZoneSelectorProps> = ({
  travelReasons,
  onSelect,
  name,
  labelID
}) => {
  const [disabledCategories, setDisabledCategories] = useState<string[]>([]);
  const zoneContent = getZoneContent(labelID);
  const [{ value: selected }, meta] = useField(name);

  useEffect(() => {
    (async () => {
      const zones = await permitService.lookupUnavailableZones();
      setDisabledCategories(zones);
    })();
  }, []);

  return (
    <>
      <Text.Heading.H3 fontWeight='bold' aria-invalid={meta.touched && !selected}>
        Select your permit type
      </Text.Heading.H3>
      <Styled.Spacer16 />
      <IconList
        style={iconListContainerStyle}
        items={travelReasons.categories.map(travelCategory => {
          const disabled = disabledCategories.indexOf(travelCategory.category) > -1;
          return {
            icon: <div dangerouslySetInnerHTML={{ __html: travelCategory.icon }} />,
            id: `assess.travelZone_${travelCategory.category}`,
            'data-testid': `${travelCategory.category}-ZoneRadioButton`,
            value: travelCategory.category,
            label: `${travelCategory.label}${disabled ? ' — Not available' : ''}`,
            disabled,
            primaryColor: travelCategory.primaryColor,
            secondaryColor: travelCategory.secondaryColor,
            ...(onSelect ? { onChange: () => onSelect(travelCategory.category) } : {})
          };
        })}
        name={name}
        errorLabelProps={iconListErrorProps}
      />
      <Styled.Spacer16 />
      {!!selected && zoneContent[selected]}
    </>
  );
};

export default TravelZoneSelector;
